import { useContext, useState, useMemo } from "react";
import Flatpickr from "react-flatpickr";
import { AppContext } from "../../../../../common";
import Loading from "../../../../../common/filter-table/Loading";
import Table from "../../../../../common/filter-table/Table";
import { getMonths } from "../../../../../utils";
import { useGetPreliquidateOvertimeListByUser } from "../hooks/useGetPreliquidateOvertimeListByUser";
import { Overtime, OvertimePreliquidate } from "../models/OvertimePreliquidateModel";
import { StatusIndicator } from "./StatusIndicator";
import { PayrollOvertimeStatus } from "../models/PayrollOvertimeStatus";

interface HourTypeTotal {
    hourType: string;
    totalHours: number;
}

export const OvertimesTable = () => {
    const { lang } = useContext(AppContext);
    const [overtimeData, setOvertimeData] = useState<OvertimePreliquidate>();
    const [selectedHourType, setSelectedHourType] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const getMutation = useGetPreliquidateOvertimeListByUser();

    const formatDate = (date: Date) => date.toISOString().split('T')[0];

    const handleDateChange = (dates: Date[]) => {
        if (!dates || dates.length < 2) {
            return;
        }
        
        const [start, end] = dates.map(formatDate);

        setIsLoading(true);

        getMutation.mutate({ startDate: start, endDate: end },{
            onSuccess: (response) => {
                setOvertimeData(response);
                setIsLoading(false);
            },
            onError: () => setIsLoading(false),
        });
    };

    const groupOvertimesByDate = (entries?: Overtime[]): Record<string, Overtime[]> => {
        return (entries ?? []).reduce((acc, entry) => {
            const key = formatDate(new Date(entry.date));

            acc[key] = acc[key] || [];
            acc[key].push(entry);

            return acc;
        }, {} as Record<string, Overtime[]>);
    };

    const filteredOvertimes = useMemo(() => {
        return overtimeData?.overtimesPreliquidations.filter(o =>
            !selectedHourType || o.hourType === selectedHourType
        );
    }, [overtimeData, selectedHourType]);

    const groupedOvertimes = useMemo(() => groupOvertimesByDate(filteredOvertimes), [filteredOvertimes]);

    const hourTypeOptions = useMemo(() => {
        const unique = new Set(overtimeData?.overtimesPreliquidations.map(o => o.hourType));
        return Array.from(unique);
    }, [overtimeData]);

    const getTotalHoursByHourType = (entries?: Overtime[]): Record<string, HourTypeTotal> => {
        return (entries ?? []).reduce((acc, entry) => {
           const type = entry.hourType;

            if(!acc[type]){
                acc[type] = { hourType: type, totalHours: 0 };
            }

            acc[type].totalHours  += entry.totalHours ?? 0;

            return acc
        }, {} as Record<string, HourTypeTotal>);
    };

    const hourTypeTotals: HourTypeTotal[] = Object.values(getTotalHoursByHourType(filteredOvertimes));

    const getTotalTime = (items: any[]) => {
        let totalHours = items.reduce((sum, item) => sum +(item.hours || 0),0);
        let totalMinutes = items.reduce((sum, item) => sum +(item.minutes || 0),0);

        const extraHours = Math.floor(totalMinutes / 60);
        const remainingMinutes = totalMinutes % 60;

        totalHours += extraHours;

        return `${totalHours}:${remainingMinutes}`
    }

    return (
        <>
            <div className="col-12 ps-2 mb-6 col-md-6">
                <h2 className="text-primary2 fw-bold mb-2">Extras y recargos</h2>
                <p className="text-muted mb-3">
                    Consulta las horas extras registradas según el rango de fechas seleccionado.
                </p>
                <div className="d-flex flex-wrap gap-3 align-items-end">
                    <div className="flex-grow-1">
                        <label className="form-label fw-semibold" htmlFor="picker-date">
                            Fecha Inicial / Fecha Final
                        </label>
                        <div className="date__picker custom">
                            <Flatpickr
                                id="picker-date"
                                lang="es"
                                className="form-control picker text-black select__control"
                                options={{
                                    mode: "range",
                                    dateFormat: "Y-m-d",
                                    locale: { months: getMonths(lang) },
                                }}
                                onChange={handleDateChange}
                                placeholder="YYYY-MM-DD"
                            />
                        </div>
                    </div>
                    <div className="flex-grow-1">
                        <label className="form-label fw-semibold" htmlFor="hourTypeFilter">
                            Filtrar por tipo de hora
                        </label>
                        <select
                            id="hourTypeFilter"
                            className="form-select"
                            value={selectedHourType}
                            onChange={(e) => setSelectedHourType(e.target.value)}
                        >
                            <option value="">Todas</option>
                                {hourTypeOptions.map((type) => (
                                    <option key={type} value={type}>
                                        {type}
                                    </option>
                                ))}
                        </select>
                    </div>
                </div>
            </div>
            <br />                   
            <div>
                {(!filteredOvertimes || filteredOvertimes.length === 0) ? (
                    <br />
                ) : (
                    <div className="table-responsive" style={{ maxWidth: '800px' }}>
                        <Table
                            columns={[
                                { isSortable: true, name: 'typeHour', title: 'Tipo de hora' },
                                { isSortable: false, name: 'total', title: 'Total' },
                            ]}
                        >
                            {isLoading ? (
                                <Loading hasSkeleton skeletonType="table" />
                            ) : (
                                hourTypeTotals.map((item) => (
                                    <tr key={`summary-${item.hourType}`} className="align-middle">
                                        <td className="bg-light fw-bold">{item.hourType}</td>
                                        <td className="bg-light">{item.totalHours} horas</td>
                                    </tr>
                                ))
                            )}
                        </Table>
                    </div>
                )}
            </div> 
            <br />
            <div className="table-responsive">
                <Table
                    columns={[
                        { isSortable: true, name: 'date', title: 'Fecha' },
                        { isSortable: false, name: 'dailyTotalHours', title: 'Total de horas del día' },
                        { isSortable: false, name: 'hourType', title: 'Tipo de Hora' },
                        { isSortable: true, name: 'hourTypeName', title: 'Nombre de Hora' },
                        { isSortable: false, name: 'totalHours', title: 'Total de horas' },
                        { isSortable: false, name: 'status', title: 'Estado' },
                        { isSortable: false, name: 'comments', title: 'Observaciones' },
                    ]}
                >
                {isLoading ? (
                    <Loading hasSkeleton skeletonType="table" />
                ) : !filteredOvertimes || filteredOvertimes.length === 0 ? (
                    <tr>
                        <td colSpan={6} className="text-center text-muted py-3">
                            No hay horas extras registradas.
                        </td>
                    </tr>
                ) : (
                        Object.entries(groupedOvertimes)
                            .sort(([a], [b]) => new Date(b).getTime() - new Date(a).getTime())
                            .flatMap(([date, items]) =>
                                items.map((overtime, index) => (
                                    <tr key={overtime.overtimeId} className="align-middle">
                                        {index === 0 && (
                                            <>
                                                <td rowSpan={items.length} className="fw-bold bg-light">
                                                    {new Date(date).toLocaleDateString('es-ES')}
                                                </td>
                                                <td rowSpan={items.length} className="fw-bold  bg-light"> 
                                                    {getTotalTime(items)}
                                                </td>
                                            </>
                                        )}
                                        <td className="bg-light">{overtime.hourType}</td>
                                        <td className="bg-light">{overtime.hourTypeName}</td>
                                        <td className="bg-light">{overtime.hours}:{overtime.minutes}</td>
                                        <td className="bg-light">
                                            <StatusIndicator
                                                status={overtime.statusDescription ?? ''}
                                                statusList={PayrollOvertimeStatus}
                                                showName
                                            />
                                        </td>
                                        <td className="bg-light">{overtime.comments || ''}</td>
                                    </tr>
                                ))
                            )
                    )}
                </Table>
            </div>
        </>
    );
};